import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const ODOO_BASE_URL = 'https://erp.kcs-tech.com:8074';

const colors = {
  pageBg: '#FFFFFF',
  navy: '#001534',
  red: '#E4002B',
  white: '#FFFFFF',
  body: '#4B5563',
  muted: '#6B7280',
  line: '#E5E7EB',
  softBlue: '#EAF2FF',
  softRed: '#FFF1F3',
  success: '#0F766E',
  error: '#C0392B',
};

function getJobTitle(job) {
  return job?.title || job?.name || 'Open Role';
}

export default function JobDescription() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(location.state?.job || null);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${ODOO_BASE_URL}/api/careers/jobs/${id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setDetail(data);
          if (!job) {
            setJob(data);
          }
        }
      })
      .catch(() => { /* error handling */ });
    return () => {
      cancelled = true;
    };
  }, [id, job]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    const formData = new FormData(e.currentTarget);
    const marketingConsent = formData.get('marketingConsent');

    if (!marketingConsent) {
      setErrorMsg('Please agree to the consent terms before submitting.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    formData.append('job_id', String(id));

    try {
      const res = await fetch(`${ODOO_BASE_URL}/api/careers/apply`, {
        method: 'POST',
        body: formData,
      });

      const responseText = await res.text();
      let data = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {};
      }

      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Submission failed. Please try again.');
      setStatus('error');
    }
  };

  if (!job && !detail) {
    return <div className="min-h-screen p-8 text-center" style={{ backgroundColor: colors.pageBg }}>Loading...</div>;
  }

  const parseDescription = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      // Highlight lines that look like headings (ending with a colon, or all caps)
      const isHeading = trimmed.length > 2 && (trimmed.endsWith(':') || trimmed === trimmed.toUpperCase());
      if (isHeading) {
        return (
          <span key={idx} className="block mt-6 mb-2 text-lg font-bold" style={{ color: colors.navy }}>
            {trimmed}
          </span>
        );
      }
      return (
        <span key={idx} className="block mb-2">
          {line}
        </span>
      );
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.pageBg, color: colors.navy }}>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-12 items-start">
        
        {/* Left Column: Job Description (Normal page flow) */}
        <div className="flex-1 w-full">
          <button
            type="button"
            className="mb-8 flex items-center text-sm font-semibold hover:underline"
            style={{ color: colors.navy }}
            onClick={() => navigate(-1)}
          >
            &larr; Back to Open Positions
          </button>
          
          <div className="mb-8">
            <span className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: colors.red }}>
              Apply for
            </span>
            <h1 className="mt-2 text-4xl font-bold leading-tight" style={{ color: colors.navy }}>
              {getJobTitle(job || detail)}
            </h1>
          </div>

          {detail?.description_text ? (
            <div className="text-base leading-relaxed" style={{ color: colors.body }}>
              {parseDescription(detail.description_text)}
            </div>
          ) : (
            <div className="text-base leading-relaxed" style={{ color: colors.body }}>
              No detailed description provided for this role.
            </div>
          )}
        </div>

        {/* Right Column: Form (Card UI) */}
        <div className="w-full lg:w-[480px] xl:w-[500px] shrink-0 sticky top-8">
          <div className="rounded-xl border p-6 sm:p-8 shadow-lg" style={{ backgroundColor: colors.white, borderColor: colors.line }}>
            <h3 className="text-xl font-bold mb-6" style={{ color: colors.red }}>Submit Your Application</h3>
            
            {status === 'success' ? (
              <div className="text-center py-4">
                <span
                  className="mb-4 inline-flex rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ backgroundColor: '#E9FBF8', color: colors.success }}
                >
                  Submitted Successfully
                </span>
                <h2 className="text-xl font-bold mt-2" style={{ color: colors.navy }}>
                  Application received
                </h2>
                <p className="mt-3 text-sm leading-6" style={{ color: colors.body }}>
                  Thanks for applying! Our team will review your profile and get in touch.
                </p>
                <button
                  type="button"
                  className="mt-6 w-full rounded-md px-5 py-3 text-sm font-semibold"
                  style={{ backgroundColor: colors.navy, color: colors.white }}
                  onClick={() => navigate('/careers/open-positions')}
                >
                  Return to Jobs
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField label="Full name" name="name" type="text" placeholder="Full name" required />
                  <FormField label="Email" name="email" type="email" placeholder="you@example.com" required />
                  <FormField label="Mobile" name="mobile" type="tel" maxLength={32} placeholder="+91 98765 43210" required />
                  <FormField label="Current location" name="current_location" type="text" maxLength={128} placeholder="City" required />
                  <div className="md:col-span-2">
                    <FormField label="LinkedIn profile" name="linkedin" type="text" maxLength={255} placeholder="linkedin.com/in/your-name" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold" style={{ color: colors.navy }}>
                      Notice period
                      <select
                        name="notice_period"
                        defaultValue=""
                        required
                        className="mt-2 w-full rounded-md border px-4 py-3 text-sm outline-none cursor-pointer focus:border-blue-500"
                        style={{ backgroundColor: colors.white, borderColor: colors.line, color: colors.body }}
                      >
                        <option value="">Select notice period</option>
                        <option value="immediate">Immediate</option>
                        <option value="15_days">15 days</option>
                        <option value="30_days">30 days</option>
                        <option value="45_days">45 days</option>
                        <option value="60_days">60 days</option>
                        <option value="90_days">90 days</option>
                        <option value="over_90_days">More than 90 days</option>
                      </select>
                    </label>
                  </div>
                </div>

                <label className="block text-sm font-semibold" style={{ color: colors.navy }}>
                  Resume (PDF, DOC, DOCX)
                  <input
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    required
                    className="mt-2 block w-full rounded-md border px-4 py-2.5 text-sm outline-none focus:border-blue-500"
                    style={{ backgroundColor: colors.white, borderColor: colors.line, color: colors.body }}
                  />
                </label>

                <label className="block text-sm font-semibold" style={{ color: colors.navy }}>
                  Cover message (optional)
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={5000}
                    placeholder="Write a brief cover note"
                    className="mt-2 w-full rounded-md border px-4 py-3 text-sm outline-none focus:border-blue-500"
                    style={{ backgroundColor: colors.white, borderColor: colors.line, color: colors.body }}
                  />
                </label>

                <input name="website_hp" type="text" autoComplete="off" tabIndex={-1} style={{ display: 'none' }} aria-hidden="true" />

                <label
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-4 text-sm mt-2"
                  style={{ backgroundColor: '#FAFAFA', borderColor: colors.line, color: colors.body }}
                >
                  <input
                    name="marketingConsent"
                    type="checkbox"
                    value="true"
                    required
                    className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
                    style={{ accentColor: colors.red }}
                  />
                  <span>
                    I agree to receive career updates and hiring communication from Kapil Technologies.
                  </span>
                </label>

                {status === 'error' && (
                  <p className="rounded-md px-4 py-3 text-sm font-medium" style={{ backgroundColor: '#FFF1F1', color: colors.error }}>
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full rounded-md px-8 py-3 text-sm font-semibold transition duration-300 disabled:cursor-not-allowed disabled:opacity-70 mt-4 shadow-sm hover:shadow-md"
                  style={{ backgroundColor: colors.red, color: colors.white }}
                >
                  {status === 'submitting' ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, name, type, placeholder, required, maxLength }) {
  return (
    <label className="block text-sm font-semibold" style={{ color: colors.navy }}>
      {label}
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        className="mt-2 w-full rounded-md border px-4 py-3 text-sm outline-none focus:border-blue-500"
        style={{ backgroundColor: colors.white, borderColor: colors.line, color: colors.body }}
      />
    </label>
  );
}

